// Prenotazioni scritte nel database. Ogni cambio = prenotazione + evento nello storico, nello stesso lotto:
// si salvano subito sul telefono e partono da sole quando torna la rete (offline-first).
// Regola S1 (niente «falsi ok»): «arrivato» (e «salvato») si mostra solo quando il server ha confermato.
// I dati di salute NON entrano mai nella prenotazione né nello storico: stanno in «salute/<idOspite>»
// (solo personale SPA, GDPR art. 9). La prenotazione tiene solo l'id della scheda (campo «ospite»).
import {
  collection, doc, writeBatch, serverTimestamp, deleteField, onSnapshot, query, where,
} from 'firebase/firestore';
import { aggiungiRisposta } from './ponte.js';

// Campi della prenotazione che si salvano (gli stessi permessi da firestore.rules).
const CAMPI = ['data', 'inizio', 'durata', 'tr', 'sala', 'lettino', 'op', 'camera', 'nome', 'tipo', 'importo', 'listino',
  'motivo', 'motivoSconto', 'magg', 'coppia', 'stato', 'addebito', 'addebitoNoShow', 'est', 'ospite', 'nota'];

function pulisci(p) {
  const out = {};
  for (const k of CAMPI) if (p[k] !== undefined) out[k] = p[k];
  return out;
}

/** Id della scheda salute di una prenotazione: il campo «ospite», o l'id della prenotazione (prenotazioni vecchie). */
export const idSalute = (b) => b.ospite || b.id;
/** Scheda salute senza niente dentro (non serve crearla). */
export const saluteVuota = (x) => !x || (!x.grav && !x.all && !String(x.note || '').trim());
/** Solo i campi permessi dalle regole: gravidanza, allergie, note (max 300). */
const schedaSalute = (x, uid) => ({
  grav: !!x?.grav, all: !!x?.all, note: String(x?.note || '').trim().slice(0, 300), da: uid, aggiornato: serverTimestamp(),
});

export function creaPrenotazioni({ db, sid, uid, scatola, nome = '' }) {
  const base = `strutture/${sid}`;
  const ascoltatori = new Set();
  // idEvento -> { prenotazione, tipo, stato: 'telefono' | 'arrivato' | 'errore', codice }
  const invii = new Map();

  function avvisa() { for (const f of ascoltatori) f(invii); }
  function segna(id, dati) { invii.set(id, { ...invii.get(id), ...dati }); avvisa(); }
  // Evento dello storico: chi (telefono e nome), cosa, quando. Mai dati di salute.
  function evento(b, tipo, extra) {
    const ev = doc(collection(db, `${base}/eventi`));
    b.set(ev, { tipo, ...extra, da: uid, daNome: String(nome).slice(0, 40), quando: serverTimestamp() });
    return ev;
  }

  /**
   * Manda il lotto. Sul telefono è già salvato; «conferma» si risolve solo quando il server ha davvero ricevuto
   * (anche dopo ore senza rete): { ok: true } oppure { ok: false, codice }. Non va mai in errore.
   */
  function invia(b, eventi, info) {
    eventi.forEach((id, i) => segna(id, { prenotazione: info.di?.[i] ?? null, tipo: info.tipo, stato: 'telefono' }));
    const conferma = b.commit().then(
      () => {
        eventi.forEach((id) => segna(id, { stato: 'arrivato' }));
        return { ok: true };
      },
      (e) => {
        const codice = e?.code || 'sconosciuto';
        eventi.forEach((id) => segna(id, { stato: 'errore', codice }));
        scatola?.registra(e, { dove: info.dove || 'prenotazione', tipo: info.tipo, prenotazioni: info.prenotazioni || [] });
        return { ok: false, codice };
      },
    );
    return { eventi, conferma };
  }

  /**
   * Scrive una o più prenotazioni (la coppia: due) con un evento ciascuna, in un solo lotto.
   * @param {{ id?: string, dati: object, togli?: string[], salute?: object|null, ospite?: string }[]} voci senza id =
   *   prenotazione nuova · togli = campi da cancellare (non vanno nell'evento) ·
   *   salute = scheda da scrivere (solo personale SPA: le regole la vietano al ricevimento)
   * @param {string} tipo 'crea' | 'modifica' | 'stato' …
   * @param {function|null} aggiungi altre scritture nello stesso lotto (la risposta al ponte): aggiungi(b, ids, evento) → id evento
   * @returns {{ eventi: string[], prenotazioni: string[], conferma: Promise<{ ok: boolean, codice?: string }> }}
   */
  function scrivi(voci, tipo, nota = '', aggiungi = null) {
    const b = writeBatch(db);
    const eventi = [];
    const di = []; // prenotazione di ogni evento
    const ids = [];
    for (const v of voci) {
      const ref = v.id ? doc(db, `${base}/prenotazioni/${v.id}`) : doc(collection(db, `${base}/prenotazioni`));
      const dati = pulisci(v.dati);
      // Ogni prenotazione nuova ha la sua scheda salute: id = id della prenotazione (vuota finché non serve).
      if (!v.id && !dati.ospite) dati.ospite = ref.id;
      const ev = evento(b, tipo, { prenotazione: ref.id, dopo: dati, nota: String(nota).slice(0, 200) });
      b.set(ref, {
        ...dati,
        ...Object.fromEntries((v.togli || []).map((k) => [k, deleteField()])),
        ...(v.id ? {} : { creata: serverTimestamp() }),
        ultimoEvento: ev.id,
        aggiornato: serverTimestamp(),
      }, { merge: true });
      eventi.push(ev.id);
      di.push(ref.id);
      ids.push(ref.id);
      if (v.salute) {
        b.set(doc(db, `${base}/salute/${dati.ospite || v.ospite || ref.id}`), schedaSalute(v.salute, uid));
        eventi.push(evento(b, 'salute', { prenotazione: ref.id }).id);
        di.push(ref.id);
      }
    }
    if (aggiungi) {
      eventi.push(aggiungi(b, ids, evento));
      di.push(ids[0]);
    }
    return { prenotazioni: ids, ...invia(b, eventi, { tipo, prenotazioni: ids, di }) };
  }

  return {
    /**
     * Nuova prenotazione (1 persona) o coppia (2 persone, stesso id di coppia).
     * @param {{ salute?: (object|null)[], aggiungi?: function }} [opz] scheda salute di ogni persona (null = niente) ·
     *   aggiungi = altre scritture nello stesso lotto (vedi scrivi)
     */
    crea(candidati, { salute = [], aggiungi = null } = {}) {
      const coppia = candidati.length === 2 ? doc(collection(db, `${base}/prenotazioni`)).id : null;
      return scrivi(candidati.map((c, i) => ({
        dati: { ...c, coppia, stato: 'confermata', addebito: false },
        salute: salute[i] || null,
      })), 'crea', '', aggiungi);
    },
    /** Modifica di una persona (anche se è in coppia). `salute` solo se il telefono può scriverla. */
    modifica(id, dati, { nota = '', salute = null, ospite = id, aggiungi = null } = {}) {
      return scrivi([{ id, dati, salute, ospite }], 'modifica', nota, aggiungi);
    },
    /**
     * Risposta a una richiesta di RVC senza toccare le prenotazioni («altro orario», «rifiutata», o «confermata» quando
     * le prenotazioni legate sono già giuste). Parte per RVC solo dopo la conferma del database.
     */
    rispondiPonte(r, stato, { proposta = null, prenotazioni = null } = {}) {
      const b = writeBatch(db);
      const ev = aggiungiRisposta(b, { db, sid }, r, stato, { proposta, prenotazioni, evento });
      return invia(b, [ev], { tipo: 'ponte', dove: 'ponte-risposta', di: [null] });
    },
    /** La stessa risposta dentro il lotto di una prenotazione nuova o cambiata: crea(…, { aggiungi: rispostaNelLotto(…) }). */
    rispostaNelLotto(r, stato) {
      return (b, ids, ev) => aggiungiRisposta(b, { db, sid }, r, stato, { prenotazioni: ids, evento: ev });
    },
    /**
     * Cambio di stato. L'operatrice manda solo lo stato; la disdetta porta anche «addebito» (regola delle 24 ore);
     * il no-show porta «addebitoNoShow» solo se lo scrive la responsabile. Tornando a «confermata» l'importo del no-show si toglie.
     * @param {{ addebito?: boolean, addebitoNoShow?: number, nota?: string }} [extra]
     */
    cambiaStato(id, stato, { addebito, addebitoNoShow, nota = '' } = {}) {
      const dati = { stato };
      if (addebito !== undefined) dati.addebito = addebito;
      if (addebitoNoShow !== undefined) dati.addebitoNoShow = addebitoNoShow;
      return scrivi([{ id, dati, togli: stato === 'confermata' ? ['addebitoNoShow'] : [] }], 'stato', nota);
    },
    /** La responsabile scrive (o cambia) quanto si addebita per un no-show già segnato. Evento «addebito» nello storico. */
    decidiNoShow(id, importo) {
      return scrivi([{ id, dati: { addebitoNoShow: importo } }], 'addebito');
    },
    /** Scheda salute di una prenotazione (solo personale SPA), con il suo evento senza dati di salute. */
    salvaSalute(prenotazione, salute) {
      const b = writeBatch(db);
      b.set(doc(db, `${base}/salute/${idSalute(prenotazione)}`), schedaSalute(salute, uid));
      const ev = evento(b, 'salute', { prenotazione: prenotazione.id });
      return invia(b, [ev.id], { tipo: 'salute', dove: 'salute', prenotazioni: [prenotazione.id], di: [prenotazione.id] });
    },
    /**
     * Turni e P.P del giorno (solo responsabile e sviluppo). `nomi` = nomi delle operatrici cambiati (valgono per tutti i giorni).
     * @param {string} data 'AAAA-MM-GG'
     * @param {{ turni: object, pause: object }} giorno
     * @param {object|null} nomi { op1: 'Nome', … } oppure null
     */
    salvaGiorno(data, { turni, pause }, nomi = null) {
      const b = writeBatch(db);
      const ev = evento(b, 'turni', { giorno: data, dopo: { turni, pause, ...(nomi && { nomi }) } });
      b.set(doc(db, `${base}/giorni/${data}`), { turni, pause, ultimoEvento: ev.id, aggiornato: serverTimestamp() });
      // mergeFields: il campo «operatrici» si sostituisce tutto (un nome cancellato sparisce), il resto resta com'è.
      if (nomi) b.set(doc(db, base), { operatrici: nomi }, { mergeFields: ['operatrici'] });
      return invia(b, [ev.id], { tipo: 'turni', dove: 'turni' });
    },
    invii: () => invii,
    ascolta(f) { ascoltatori.add(f); f(invii); return () => ascoltatori.delete(f); },
  };
}

// Prenotazioni del giorno in tempo reale. `inSospeso` = modifiche salvate sul telefono e non ancora arrivate.
export function ascoltaGiorno({ db, sid }, data, quando, errore) {
  const q = query(collection(db, `strutture/${sid}/prenotazioni`), where('data', '==', data));
  return onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
    const prenotazioni = snap.docs.map((d) => ({ ...d.data(), id: d.id, inSospeso: d.metadata.hasPendingWrites }));
    quando({ prenotazioni, dallaCache: snap.metadata.fromCache, inSospeso: snap.metadata.hasPendingWrites });
  }, errore);
}

// Turni e pause del giorno (documento «giorni/AAAA-MM-GG»): { turni: { op1: true… }, pause: { op1: '12:00' } }.
export function ascoltaTurni({ db, sid }, data, quando, errore) {
  return onSnapshot(doc(db, `strutture/${sid}/giorni/${data}`), (d) => quando(d.exists() ? d.data() : null), errore);
}

// Sale o lettini fuori servizio del giorno (S8). Quelli «tolti» li scarta la logica dell'agenda.
export function ascoltaBlocchi({ db, sid }, data, quando, errore) {
  const q = query(collection(db, `strutture/${sid}/blocchi`), where('data', '==', data));
  return onSnapshot(q, (snap) => quando(snap.docs.map((d) => ({ ...d.data(), id: d.id }))), errore);
}

/**
 * Tutto quello che serve per i controlli di un giorno, insieme: prenotazioni, turni, P.P e fuori servizio.
 * Chiama `quando` solo quando sono arrivati tutti e tre (prima i controlli sarebbero sbagliati).
 * @returns {() => void} ferma l'ascolto
 */
export function ascoltaGiornoIntero(ctx, data, quando, errore) {
  const x = { prenotazioni: null, giorno: undefined, blocchi: null, dallaCache: true };
  const manda = () => {
    if (!x.prenotazioni || x.giorno === undefined || !x.blocchi) return;
    quando({
      data,
      prenotazioni: x.prenotazioni,
      turni: x.giorno?.turni || null,
      pause: x.giorno?.pause || {},
      blocchi: x.blocchi,
      dallaCache: x.dallaCache,
    });
  };
  const stop = [
    ascoltaGiorno(ctx, data, ({ prenotazioni, dallaCache }) => { x.prenotazioni = prenotazioni; x.dallaCache = dallaCache; manda(); }, errore),
    ascoltaTurni(ctx, data, (g) => { x.giorno = g; manda(); }, errore),
    ascoltaBlocchi(ctx, data, (b) => { x.blocchi = b; manda(); }, errore),
  ];
  return () => stop.forEach((f) => f());
}

// Una prenotazione sola, in tempo reale (scheda aperta). null = non c'è.
export function ascoltaPrenotazione({ db, sid }, id, quando, errore) {
  return onSnapshot(doc(db, `strutture/${sid}/prenotazioni/${id}`), { includeMetadataChanges: true },
    (d) => quando(d.exists() ? { ...d.data(), id: d.id, inSospeso: d.metadata.hasPendingWrites } : null), errore);
}

// Storico di una prenotazione (dal più vecchio). L'ordine si fa sul telefono: niente indice in più nel database.
export function ascoltaStorico({ db, sid }, id, quando, errore) {
  const q = query(collection(db, `strutture/${sid}/eventi`), where('prenotazione', '==', id));
  return onSnapshot(q, (snap) => {
    const ms = (e) => (e.quando?.toMillis ? e.quando.toMillis() : Number.MAX_SAFE_INTEGER);
    quando(snap.docs.map((d) => ({ ...d.data({ serverTimestamps: 'estimate' }), id: d.id })).sort((a, b) => ms(a) - ms(b)));
  }, errore);
}

// Scheda salute (solo personale SPA: il ricevimento non deve nemmeno chiederla). null = ancora vuota.
export function ascoltaSalute({ db, sid }, idOspite, quando, errore) {
  return onSnapshot(doc(db, `strutture/${sid}/salute/${idOspite}`), (d) => quando(d.exists() ? d.data() : null), errore);
}
